// src/shared/services/ApiClient.js

import { API_URL } from '../../config/env';

/**
 * Minimal API client for the current scope:
 * - getForm(accessKey, slug): fetch JSON Schema form definition for a task access key.
 *
 * Notes:
 * - Uses API_URL from src/config/env as the base.
 * - Throws rich errors (with status) for non-2xx responses.
 * - No other endpoints included yet to avoid unused code in the codebase.
 */

function buildFormUrl(accessKey, slug) {
  const ak = encodeURIComponent(accessKey);
  const s = encodeURIComponent(slug);
  return `${API_URL}/v1/assets/access_key/${ak}/forms/${s}`;
}

/**
 * Internal JSON fetch helper that:
 * - sets JSON headers
 * - throws an Error with { status, body } on non-OK
 */
async function getJson(url, { signal } = {}) {
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
    signal,
  });

  const text = await res.text();
  const body = text ? safeJsonParse(text) : null;

  if (!res.ok) {
    const err = new Error(
      `Request failed (${res.status}${res.statusText ? ` ${res.statusText}` : ''})`
    );
    err.status = res.status;
    err.body = body;
    throw err;
  }

  return body;
}

function safeJsonParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Fetch a form definition for a workflow step.
 * Endpoint: /v1/assets/access_key/:access_key/forms/:slug
 *
 * @param {string} accessKey - Task access key
 * @param {string} slug - FormDefinition slug
 * @param {AbortSignal} [signal] - optional AbortController signal
 * @returns {Promise<Object>} { schema, uiSchema, formData, title, description, meta, slug }
 */
async function getForm(accessKey, slug, { signal } = {}) {
  const url = buildFormUrl(accessKey, slug);
  return getJson(url, { signal });
}

const ApiClient = {
  getForm,
};

export default ApiClient;
